import { call, put, select, takeLatest } from 'redux-saga/effects';
import { sapConfigurationActions as actions } from '.';
import {
  selectErpSystem,
  selectPlantCode,
  selectSalesDocumentNumber,
  selectSalesDocumentPosition,
  selectSapConfigurationNumber,
} from 'app/features/erpSystem/slice/selectors';
import { getChosenItemConf, getProductionText, getSapConfDesc } from './utils';
import { selectSelectedItem } from 'app/features/selectedItem/slice/selectors';
import { configuratorType } from 'constants/index';
import { selectQuoteInformation } from 'app/features/quote/slice/selectors';
import { selectBasicForm } from 'app/features/selectedFilters/slice/selectors';
import { PayloadAction } from '@reduxjs/toolkit';
import { SapConfigurationInput } from './types';
import { selectInserts } from 'app/features/inserts/slice/selectors';
import { selectMountingPart } from 'app/features/mountingPart/slice/selectors';
import { selectMountingApply } from 'app/features/configuratorData/slice/selectors';
import { selectStemExtensionsData } from 'app/features/stemExtensions/slice/selectors';
import { selectPucks } from 'app/features/pucks/slice/selectors';
import { selectSpeedControlBlockData } from 'app/features/speedControlBlock/slice/selectors';
import { selectMountingPlate } from 'app/features/mountingPlate/slice/selectors';
import {
  selectBracket,
  selectCoupling,
} from 'app/features/couplings/slice/selectors';

function* sendConfigurationToSap(action: PayloadAction<string>) {
  const { sendSapConfigurationSuccess, sendSapConfigurationFail } = actions;
  const salesTextDesc: string = action.payload;

  try {
    const erpSystem = yield select(selectErpSystem);
    const plantCode = yield select(selectPlantCode);
    const salesDocumentNumber = yield select(selectSalesDocumentNumber);
    const salesDocumentPosition = yield select(selectSalesDocumentPosition);
    const sapConfigurationNumber = yield select(selectSapConfigurationNumber);
    const selectedItems = yield select(selectSelectedItem);
    const selectedInserts = yield select(selectInserts);
    const productionInfo = yield select(selectQuoteInformation);
    const actuatorType = yield select(state => selectBasicForm(state, 2));
    const selectedMountingPart = yield select(selectMountingPart);
    const isMountingApply = yield select(selectMountingApply);
    const selectedStemExtensions = yield select(selectStemExtensionsData);
    const selectedPucks = yield select(selectPucks);
    const selectedSpeedControlBlock = yield select(selectSpeedControlBlockData);
    const selectedMountingPlate = yield select(selectMountingPlate);
    const selectedCoupling = yield select(selectCoupling);
    const selectedBracket = yield select(selectBracket);

    const itemConfig = getChosenItemConf(
      selectedItems,
      selectedInserts,
      [selectedMountingPart],
      isMountingApply,
      selectedStemExtensions,
      selectedPucks,
      selectedSpeedControlBlock,
      [selectedMountingPlate],
      selectedCoupling,
      selectedBracket,
    );

    const payload: SapConfigurationInput = {
      configuratorType: configuratorType,
      erpSystem,
      plantCode,
      salesDocumentNumber,
      salesDocumentPosition,
      sapConfigurationNumber,
      configurableMaterialNumber: selectedItems[1].item.configuration,
      configurationIdDriveWorks: sapConfigurationNumber,
      sapConfiguration: {
        description: getSapConfDesc(
          selectedItems[1].item.attributes,
          selectedItems[2].item.attributes,
          actuatorType,
          erpSystem,
        ),
        salesSetupTime: productionInfo.data.setupTime,
        productionTime: productionInfo.data.laborTime,
        workCenter: productionInfo.data.route,
        assemblySalesDescription: salesTextDesc,
        productionText: getProductionText(selectedItems[5].item.services),
      },
      itemConfiguration: itemConfig,
    };

    const sendConfigToSapResponse = yield call(
      fetch,
      `/valve-configurator/api/sapConfiguration`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );

    if (sendConfigToSapResponse.status !== 200) {
      return yield put(sendSapConfigurationFail('failed to fetch'));
    } else {
      const data: any = yield sendConfigToSapResponse.json();
      return yield put(sendSapConfigurationSuccess(data));
    }
  } catch (e) {
    yield put(sendSapConfigurationFail(e));
  }
}

export function* sapConfigurationSaga() {
  yield takeLatest(actions.sendSapConfiguration.type, sendConfigurationToSap);
}
